import { api } from './api';

const notificationsBase = `${process.env.REACT_APP_API_URL}/notifications`;

export type NotificationItem = {
  id: number;
  kind: string;
  title: string;
  body: string;
  data: string;
  scheduled_for: string | null;
  sent_at: string | null;
  read_at: string | null;
  created_at: string;
};

export type NotificationsListResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: NotificationItem[];
};

export type NotificationPreferences = {
  event_reminders: boolean;
  go_now: boolean;
  flight_status: boolean;
  subscription: boolean;
  buddy_invites: boolean;
};

export type NotificationPreferencesPatch = Partial<NotificationPreferences>;

export type NotificationDevice = {
  id: number;
  platform: string;
  is_active: boolean;
  last_seen_at: string;
  created_at: string;
};

export type RegisterNotificationDevicePayload = {
  fcm_token: string;
  platform: 'web';
};

export type ReminderConfig = {
  offsets: string[];
  start: boolean;
};

const tripReminderTag = (tripId: number) => ({ type: 'TripReminder' as const, id: tripId });
const tripStopReminderTag = (stopId: number) => ({ type: 'TripStopReminder' as const, id: stopId });

export const notificationsApi = api.injectEndpoints({
  endpoints: builder => ({
    getNotifications: builder.query<NotificationsListResponse, { page: number; page_size: number }>({
      query: ({ page, page_size }) => ({
        url: `${notificationsBase}/`,
        params: { page, page_size },
      }),
      providesTags: [{ type: 'Notification', id: 'LIST' }],
    }),
    markNotificationRead: builder.mutation<void, number>({
      query: id => ({
        url: `${notificationsBase}/${id}/read/`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'Notification', id: 'LIST' }],
    }),
    getNotificationPreferences: builder.query<NotificationPreferences, void>({
      query: () => ({ url: `${notificationsBase}/preferences/` }),
      providesTags: ['NotificationPreferences'],
    }),
    patchNotificationPreferences: builder.mutation<NotificationPreferences, NotificationPreferencesPatch>({
      query: body => ({
        url: `${notificationsBase}/preferences/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['NotificationPreferences'],
    }),
    registerNotificationDevice: builder.mutation<NotificationDevice, RegisterNotificationDevicePayload>({
      query: body => ({
        url: `${notificationsBase}/devices/`,
        method: 'POST',
        body,
      }),
    }),
    deleteNotificationDevice: builder.mutation<void, number>({
      query: id => ({
        url: `${notificationsBase}/devices/${id}/`,
        method: 'DELETE',
      }),
    }),
    getTripReminder: builder.query<ReminderConfig, number>({
      query: tripId => ({ url: `${notificationsBase}/reminders/trip/${tripId}/` }),
      providesTags: (_r, _e, tripId) => [tripReminderTag(tripId)],
    }),
    putTripReminder: builder.mutation<ReminderConfig, { tripId: number; body: ReminderConfig }>({
      query: ({ tripId, body }) => ({
        url: `${notificationsBase}/reminders/trip/${tripId}/`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, { tripId }) => [tripReminderTag(tripId)],
    }),
    getTripStopReminder: builder.query<ReminderConfig, number>({
      query: stopId => ({ url: `${notificationsBase}/reminders/trip-stop/${stopId}/` }),
      providesTags: (_r, _e, stopId) => [tripStopReminderTag(stopId)],
    }),
    putTripStopReminder: builder.mutation<ReminderConfig, { stopId: number; body: ReminderConfig }>({
      query: ({ stopId, body }) => ({
        url: `${notificationsBase}/reminders/trip-stop/${stopId}/`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, { stopId }) => [tripStopReminderTag(stopId)],
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useMarkNotificationReadMutation,
  useGetNotificationPreferencesQuery,
  usePatchNotificationPreferencesMutation,
  useRegisterNotificationDeviceMutation,
  useDeleteNotificationDeviceMutation,
  useGetTripReminderQuery,
  usePutTripReminderMutation,
  useGetTripStopReminderQuery,
  usePutTripStopReminderMutation,
} = notificationsApi;
